import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import DailyLogPanel from "@/components/DailyLogPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_LABELS = {
  small: { label: "Small Bundle", glow: "#22c55e",   tone: "from-emerald-700 to-emerald-900"  },
  mid:   { label: "Mid Bundle",   glow: "#3b82f6",   tone: "from-sky-700 to-blue-900"          },
  big:   { label: "Grand Bundle", glow: "#f59e0b",   tone: "from-orange-600 to-red-900"        },
};

function ProgressBar({ pct, color = "#FCD34D" }) {
  return (
    <div className="h-1.5 w-full bg-black/40 overflow-hidden" style={{ borderRadius: 2 }}>
      <div className="h-full transition-all duration-500" style={{ width: `${Math.min(100, pct)}%`, background: color }} />
    </div>
  );
}

function TaskTile({ task, onClaim }) {
  const pct = (task.progress / Math.max(1, task.goal)) * 100;
  const done = task.claimed;
  const ready = task.ready_to_claim;
  const stale = !task.wired && !done && !ready;

  return (
    <div
      data-testid={`daily-task-${task.id}`}
      className={`relative p-3 border-2 transition-all ${
        done   ? "bg-emerald-900/30 border-emerald-500"
        : ready ? "bg-yellow-900/20 border-yellow-400 animate-pulse"
        : "bg-black/40 border-white/10"
      }`}
      style={{ borderRadius: 6 }}
    >
      <div className="flex items-start gap-2.5">
        <div className="text-2xl leading-none mt-0.5">{task.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-bold text-white truncate">{task.label}</span>
            {stale && (
              <span className="text-[8px] font-black uppercase tracking-widest text-orange-400/80 border border-orange-500/40 px-1 py-0.5" style={{ borderRadius: 2 }}>
                Tracking soon
              </span>
            )}
          </div>
          <div className="text-[10px] text-white/50 mt-0.5 leading-snug">{task.blurb}</div>
          <div className="mt-1.5 flex items-center gap-2">
            <ProgressBar pct={pct} color={done ? "#22c55e" : ready ? "#FCD34D" : "#64748b"} />
            <span className="text-[10px] tabular-nums text-white/60 font-mono">
              {task.progress}/{task.goal}
            </span>
          </div>
        </div>
        <button
          data-testid={`daily-claim-${task.id}`}
          onClick={() => onClaim(task.id)}
          disabled={!ready || done}
          className={`shrink-0 w-9 h-9 flex items-center justify-center text-lg font-black transition-all ${
            done   ? "bg-emerald-500 text-black"
            : ready ? "bg-[#FCD34D] text-black hover:scale-110 cursor-pointer"
            : "bg-black/60 text-white/20 border border-white/10 cursor-not-allowed"
          }`}
          style={{ borderRadius: 4 }}
          title={done ? "Already claimed" : ready ? "Touch to confirm ✔️" : "Not done yet"}
        >
          {done ? "✔" : ready ? "✓" : "○"}
        </button>
      </div>
    </div>
  );
}

function BundleCard({ tierKey, tierInfo, claimedCount, openBundle, alreadyOpened, isMonday, streakBonus = 0 }) {
  const unlocked = claimedCount >= tierInfo.threshold;
  const wasOpened = alreadyOpened &&
    (["small","mid","big"].indexOf(alreadyOpened) >= ["small","mid","big"].indexOf(tierKey));
  const meta = TIER_LABELS[tierKey];

  return (
    <div
      data-testid={`daily-bundle-${tierKey}`}
      className={`relative p-3 border-2 ${unlocked ? "border-yellow-400" : "border-white/10"} bg-gradient-to-br ${meta.tone}`}
      style={{
        borderRadius: 8,
        boxShadow: unlocked ? `0 0 24px ${meta.glow}55` : "none",
        opacity: unlocked ? 1 : 0.5,
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/70 font-bold">
            Tier · {tierInfo.threshold} done
          </div>
          <div className="text-base font-black text-white">{meta.label}</div>
        </div>
        <div className="text-3xl">{tierKey === "big" ? "🎁" : tierKey === "mid" ? "📦" : "🪙"}</div>
      </div>
      <button
        data-testid={`daily-open-${tierKey}`}
        onClick={openBundle}
        disabled={!unlocked || wasOpened}
        className={`w-full py-2 font-black text-xs uppercase tracking-widest ${
          wasOpened ? "bg-black/40 text-white/40 cursor-not-allowed"
          : unlocked ? "bg-yellow-400 text-black hover:bg-yellow-300"
          : "bg-black/40 text-white/30 cursor-not-allowed"
        }`}
        style={{ borderRadius: 4 }}
      >
        {wasOpened ? "Opened ✓" : unlocked ? "Open Bundle" : `Need ${tierInfo.threshold - claimedCount} more`}
      </button>
      {tierKey === "big" && isMonday && (
        <div className="mt-2 text-[10px] uppercase tracking-widest text-yellow-300 font-bold text-center">
          🗝️ Monday Bonus · Soul Key
        </div>
      )}
      {tierKey === "big" && streakBonus > 0 && (
        <div className="mt-1 text-[10px] uppercase tracking-widest text-orange-300 font-bold text-center">
          🔥 Streak Bonus · +{streakBonus} extra roll{streakBonus === 1 ? "" : "s"}
        </div>
      )}
    </div>
  );
}

export default function DailyCalendar() {
  const { isAuthed } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/daily-quests/state`);
      setState(data);
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthed) return;
    refresh();
  }, [isAuthed, refresh]);

  async function handleClaim(taskId) {
    try {
      const { data } = await axios.post(`${API}/daily-quests/claim`, { task_id: taskId });
      setState(data.state || state);
      toast.success("Task confirmed ✔️");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Not done yet.");
    }
  }

  async function handleOpenBundle() {
    try {
      const { data } = await axios.post(`${API}/daily-quests/open-bundle`);
      setState(data.state || state);
      const rewardsText = (data.rewards || []).map(r => `${r.icon || "•"} ${r.label}`).join(" · ");
      toast.success(`Opened ${data.tier.toUpperCase()} bundle!`, {
        description: rewardsText,
        duration: 8000,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open bundle.");
    }
  }

  if (!isAuthed) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-white/60">
        Sign in to see your Daily Calendar.
      </div>
    );
  }
  if (loading || !state) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/60">Loading the daily ledger…</div>;
  }

  const tasks = state.tasks || [];
  const completedTasks = tasks.filter(t => t.claimed).length;
  const totalTasks = state.total_tasks || tasks.length;
  const overallPct = (completedTasks / Math.max(1, totalTasks)) * 100;

  return (
    <div data-testid="daily-calendar-page" className="max-w-4xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-baseline gap-2 mb-1">
          <h1 className="text-3xl md:text-4xl font-black uppercase tracking-wider text-[#FCD34D]">
            📅 Daily Calendar
          </h1>
          <span className="text-xs uppercase tracking-widest text-white/40">
            Resets at Pacific midnight
          </span>
        </div>
        <p className="text-sm text-white/60">
          Knock out the day&apos;s tasks, tap each one to confirm ✔️, then crack open a loot bundle.
          {state.is_monday_pt && (
            <span className="text-yellow-300 font-bold"> · 🗝️ Monday: Soul Key bonus on Grand Bundle!</span>
          )}
        </p>
      </div>

      {/* 📊 Unified Daily Log — same log used across Kitchen/Gym/Tracker/AI Coach */}
      <div className="mb-6" data-testid="daily-calendar-unified-log">
        <DailyLogPanel title="Today's Food + Workouts" />
      </div>

      {/* 🔥 Sticky Streak banner */}
      {state.streak && (
        <div
          data-testid="daily-streak-banner"
          className="mb-5 flex items-center gap-3 p-3 border-2 border-orange-500/60 bg-gradient-to-r from-orange-900/30 via-red-900/30 to-orange-900/30"
          style={{ borderRadius: 8, boxShadow: state.streak.current > 0 ? "0 0 20px rgba(249,115,22,0.35)" : "none" }}
        >
          <div className="text-3xl">🔥</div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-orange-300 font-bold leading-none">
              Sticky Streak
            </div>
            <div className="text-lg font-black text-white leading-tight">
              <span data-testid="daily-streak-current" className="text-orange-400">{state.streak.current}</span>
              <span className="text-white/40 text-sm font-normal"> day{state.streak.current === 1 ? "" : "s"}</span>
              {state.streak.longest > state.streak.current && (
                <span className="ml-2 text-xs text-white/40 font-normal">
                  · longest {state.streak.longest}
                </span>
              )}
            </div>
            <div className="text-[11px] text-orange-200/80 leading-snug mt-0.5">
              {state.streak.bonus_rolls_for_big > 0 ? (
                <>
                  Today&apos;s Grand Bundle gets
                  <span className="text-yellow-300 font-bold"> +{state.streak.bonus_rolls_for_big} bonus roll{state.streak.bonus_rolls_for_big === 1 ? "" : "s"}</span>
                  {state.streak.bonus_rolls_for_big < state.streak.bonus_cap && (
                    <> · {state.streak.bonus_cap - state.streak.bonus_rolls_for_big} more day{state.streak.bonus_cap - state.streak.bonus_rolls_for_big === 1 ? "" : "s"} to cap (+{state.streak.bonus_cap}).</>
                  )}
                  {state.streak.bonus_rolls_for_big >= state.streak.bonus_cap && (
                    <span className="text-yellow-400 font-bold"> · MAXED 🔥</span>
                  )}
                </>
              ) : (
                <>Claim the Grand Bundle today to start a streak — +1 bonus roll per consecutive day (cap +{state.streak.bonus_cap}).</>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Overall progress strip */}
      <div className="mb-6 p-4 bg-black/50 border border-white/10" style={{ borderRadius: 8 }}>
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-sm font-bold text-white">Today&apos;s Progress</span>
          <span className="text-2xl font-black tabular-nums text-[#FCD34D]" data-testid="daily-progress-count">
            {completedTasks}<span className="text-white/40 text-base"> / {totalTasks}</span>
          </span>
        </div>
        <ProgressBar pct={overallPct} color="#FCD34D" />
      </div>

      {/* Bundles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        {["small","mid","big"].map((k) => (
          <BundleCard
            key={k}
            tierKey={k}
            tierInfo={state.tiers[k]}
            claimedCount={completedTasks}
            openBundle={handleOpenBundle}
            alreadyOpened={state.bundle_claimed}
            isMonday={state.is_monday_pt}
            streakBonus={state.streak?.bonus_rolls_for_big || 0}
          />
        ))}
      </div>

      {/* Task tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
        {tasks.map((t) => (
          <TaskTile key={t.id} task={t} onClaim={handleClaim} />
        ))}
      </div>

      {/* History */}
      {(state.bundles_awarded || []).length > 0 && (
        <div className="mt-8 p-4 bg-black/40 border border-white/10" style={{ borderRadius: 8 }}>
          <div className="text-xs uppercase tracking-widest text-white/60 mb-2 font-bold">Today&apos;s Spoils</div>
          {(state.bundles_awarded || []).map((b) => (
            <div key={b.bundle_id} className="text-sm text-white/80 mb-1">
              <span className="text-[10px] uppercase tracking-widest mr-2 px-1.5 py-0.5 border border-white/20" style={{ borderRadius: 2 }}>
                {b.tier}
              </span>
              {(b.rewards || []).map((r,i) => (
                <span key={i} className="mr-2">{r.icon} {r.label}</span>
              ))}
              {b.monday_bonus && <span className="ml-2 text-yellow-300">🗝️ Monday Bonus</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
